import { useState, useCallback, useEffect, useRef } from 'react';

const DEFAULT_SETTINGS = { brightness: 0, contrast: 0 };

export function useCameraFilters(isVideoWall = false) {
    const STORAGE_KEY = isVideoWall ? 'videoWallCameraBrightnessContrast' : 'transactionCameraBrightnessContrast';

    const EVENT_NAME = `local-storage-update-${STORAGE_KEY}`;

    const getInitialStorage = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Failed to parse camera filters from localStorage', error);
            return {};
        }
    };

    const [store, setStore] = useState(getInitialStorage);
    const lastKeyRef = useRef(STORAGE_KEY);

    useEffect(() => {
        if (lastKeyRef.current !== STORAGE_KEY) {
            lastKeyRef.current = STORAGE_KEY;
            setStore(getInitialStorage());
            return;
        }

        const stringifiedStore = JSON.stringify(store);

        if (localStorage.getItem(STORAGE_KEY) !== stringifiedStore) {
            localStorage.setItem(STORAGE_KEY, stringifiedStore);
            window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: store }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, STORAGE_KEY, EVENT_NAME]);

    useEffect(() => {
        const handleCustomEvent = (e) => {
            setStore(e.detail);
        };

        const handleStorageEvent = (e) => {
            if (e.key === STORAGE_KEY) {
                try {
                    setStore(e.newValue ? JSON.parse(e.newValue) : {});
                } catch (error) {
                    console.error(error);
                }
            }
        };

        window.addEventListener(EVENT_NAME, handleCustomEvent);
        window.addEventListener('storage', handleStorageEvent);

        return () => {
            window.removeEventListener(EVENT_NAME, handleCustomEvent);
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [STORAGE_KEY, EVENT_NAME]);

    const getSettings = useCallback((id) => store[id] || { ...DEFAULT_SETTINGS }, [store]);

    const updateSetting = useCallback((id, key, value) => {
        setStore((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                ...(prev[id] || DEFAULT_SETTINGS),
                [key]: parseInt(value, 10) || 0,
            },
        }));
    }, []);

    const resetCamera = useCallback((id) => {
        setStore((prev) => ({ ...prev, [id]: { ...DEFAULT_SETTINGS } }));
    }, []);

    const applyToAll = useCallback((currentId, visibleIds) => {
        setStore((prev) => {
            const currentSettings = prev[currentId] || { ...DEFAULT_SETTINGS };
            const updated = { ...prev };
            visibleIds.forEach((id) => {
                updated[id] = { ...currentSettings };
            });
            return updated;
        });
    }, []);

    const resetAll = useCallback((visibleIds) => {
        setStore((prev) => {
            const updated = { ...prev };
            visibleIds.forEach((id) => {
                updated[id] = { ...DEFAULT_SETTINGS };
            });
            return updated;
        });
    }, []);

    return { getSettings, updateSetting, resetCamera, applyToAll, resetAll };
}
